import {dollarsToCpmMicros} from './mediationDraft.ts';

export const ADMOB_NETWORK_WATERFALL_SOURCE_ID = '1215381445328257950';
/** @deprecated Prefer resolveAdmobWaterfallAdapter — Android Regular BANNER/INTERSTITIAL defaults. */
export const ADMOB_WATERFALL_ADAPTER_ID = '504';
/** @deprecated Prefer resolveAdmobWaterfallAdapter — Android Regular config key. */
export const ADMOB_OPMC_CONFIG_KEY = '535';

/** Classic AdMob Network (LIVE default). UI often labels MANUAL floors under this id. */
export const ADMOB_NETWORK_LIVE_SOURCE_ID = '5450213213286189855';

export type WaterfallAdapterIds = {
    adapterId: string;
    configKey: string;
};

/**
 * AdMob Network Waterfall adapter + pubid config key by platform/format.
 * @see https://developers.google.com/admob/api/v1/adapters-reference
 */
export function resolveAdmobWaterfallAdapter(
    platform: string | null | undefined,
    format: string | null | undefined,
): WaterfallAdapterIds {
    const ios = String(platform || '').toUpperCase().trim() === 'IOS';
    const fmt = String(format || '').toUpperCase().trim();
    if (fmt === 'REWARDED') {
        return ios
            ? {adapterId: '507', configKey: '538'}
            : {adapterId: '506', configKey: '537'};
    }
    if (fmt === 'NATIVE') {
        return ios
            ? {adapterId: '509', configKey: '540'}
            : {adapterId: '508', configKey: '539'};
    }
    if (fmt === 'REWARDED_INTERSTITIAL') {
        return ios
            ? {adapterId: '511', configKey: '542'}
            : {adapterId: '510', configKey: '541'};
    }
    if (fmt === 'APP_OPEN') {
        return ios
            ? {adapterId: '617', configKey: '754'}
            : {adapterId: '616', configKey: '753'};
    }
    // BANNER / INTERSTITIAL / unknown → Regular SDK
    return ios
        ? {adapterId: '505', configKey: '536'}
        : {adapterId: '504', configKey: '535'};
}

/**
 * True when a new row should use the OPMC waterfall chain.
 * - Waterfall source 121538… → yes (MANUAL floors)
 * - Classic AdMob Network 545021… → only if MANUAL (UI sometimes mislabels floors)
 * - LIVE → never (bidding; no OPMC)
 */
export function isAdmobWaterfallFloorSource(
    adSourceId: string | null | undefined,
    cpmMode?: string | null,
): boolean {
    const id = String(adSourceId || '').trim();
    const mode = String(cpmMode || 'MANUAL').toUpperCase();
    if (mode === 'LIVE') {
        return false;
    }
    return id === ADMOB_NETWORK_WATERFALL_SOURCE_ID || id === ADMOB_NETWORK_LIVE_SOURCE_ID;
}

export type BuildOpmcCreateChangeInput = {
    appId: string;
    displayName: string;
    format: string;
    floorDollars: number | null | undefined;
    adTypes?: string[];
};

export function buildOpmcCreateChange(input: BuildOpmcCreateChangeInput): Record<string, unknown> {
    const change: Record<string, unknown> = {
        appId: String(input.appId || '').trim(),
        displayName: String(input.displayName || '').trim(),
        format: String(input.format || '').trim(),
        adTypes: input.adTypes?.length ? [...input.adTypes] : ['RICH_MEDIA'],
    };
    const micros = dollarsToCpmMicros(input.floorDollars);
    if (micros != null) {
        change.cpmFloorSettings = {globalFloorMicros: micros};
    }
    return change;
}

export type BuildOpmcMappingChangeInput = {
    opmcId: string;
    displayName?: string;
    /** ANDROID | IOS — selects adapter/config key with format */
    platform?: string;
    format?: string;
    adapterId?: string;
    configKey?: string;
};

export function buildOpmcMappingChange(input: BuildOpmcMappingChangeInput): Record<string, unknown> {
    const resolved = resolveAdmobWaterfallAdapter(input.platform, input.format);
    const adapterId = String(input.adapterId || resolved.adapterId).trim();
    const configKey = String(input.configKey || resolved.configKey).trim();
    const change: Record<string, unknown> = {
        adapterId,
        adSourceId: ADMOB_NETWORK_WATERFALL_SOURCE_ID,
        adUnitConfigurations: {[configKey]: String(input.opmcId || '').trim()},
    };
    const displayName = String(input.displayName || '').trim();
    if (displayName) {
        change.displayName = displayName;
    }
    return change;
}

/** True when mapping change already carries a pubid configuration value. */
export function mappingChangeHasOpmcConfig(change: unknown): boolean {
    if (!change || typeof change !== 'object') {
        return false;
    }
    const configs = (change as Record<string, unknown>).adUnitConfigurations;
    if (!configs || typeof configs !== 'object') {
        return false;
    }
    return Object.values(configs as Record<string, unknown>).some(
        (v) => String(v || '').trim().length > 0,
    );
}

export type BuildWaterfallLineChangeInput = {
    tempLineId: string;
    displayName: string;
    standardAdUnitId: string;
    mappingName: string;
    cpmMicros: string;
    adSourceId?: string;
};

export function buildWaterfallLineChange(input: BuildWaterfallLineChangeInput): Record<string, unknown> {
    const tempLineId = String(input.tempLineId || '').trim();
    return {
        updateMask: `mediationGroupLines["${tempLineId}"]`,
        mediationGroupLines: {
            [tempLineId]: {
                id: tempLineId,
                displayName: String(input.displayName || '').trim(),
                adSourceId: String(input.adSourceId || ADMOB_NETWORK_WATERFALL_SOURCE_ID).trim(),
                cpmMode: 'MANUAL',
                cpmMicros: String(input.cpmMicros || '').trim(),
                state: 'ENABLED',
                adUnitMappings: {
                    [String(input.standardAdUnitId || '').trim()]: String(input.mappingName || '').trim(),
                },
            },
        },
    };
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (!value || typeof value !== 'object') {
        return null;
    }
    return value as Record<string, unknown>;
}

/** Flat resource or apply envelope `{ result: resource }`. */
function unwrapApplyResource(data: unknown): Record<string, unknown> | null {
    const root = asRecord(data);
    if (!root) {
        return null;
    }
    const nested = asRecord(root.result);
    if (nested) {
        return nested;
    }
    return root;
}

export function parseOpmcIdFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) {
        return '';
    }
    const fromField = String(record.admobNetworkWaterfallAdUnitId || '').trim();
    if (fromField) {
        return fromField;
    }
    const name = String(record.name || '').trim();
    const marker = '/adMobNetworkWaterfallAdUnits/';
    const idx = name.indexOf(marker);
    if (idx >= 0) {
        return name.slice(idx + marker.length).split('/').filter(Boolean)[0] || '';
    }
    return '';
}

export function parseMappingNameFromApplyData(data: unknown): string {
    const record = unwrapApplyResource(data);
    if (!record) {
        return '';
    }
    return String(record.name || '').trim();
}

/** OPMC id from a mapping resource's adUnitConfigurations (pubid keys). */
export function opmcIdFromMapping(mapping: unknown): string {
    const record = asRecord(mapping);
    if (!record) {
        return '';
    }
    const configs =
        asRecord(record.adUnitConfigurations) ||
        asRecord(record.ad_unit_configurations) ||
        {};
    const preferredKeys = [
        ADMOB_OPMC_CONFIG_KEY,
        '537',
        '538',
        '539',
        '540',
        '541',
        '542',
    ];
    for (const key of preferredKeys) {
        const value = String(configs[key] || '').trim();
        if (value) {
            return value;
        }
    }
    for (const value of Object.values(configs)) {
        const text = String(value || '').trim();
        if (text) {
            return text;
        }
    }
    return '';
}

export function adUnitResourceIdFromName(name: unknown): string {
    const text = String(name || '');
    const parts = text.split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1] : '';
}
